import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  fetchMedicineList,
  uploadPrescriptionApi,
  fetchCartList,
  fetchMedicineCartList,
  deleteCartPrescription,
  deleteCartItem,
  toggleCartValueApi,
  changeMedicineQuantity,
  fetchUserAddresses,
  saveUserAddress,
  placeOrderApi,
  addNewOrder,
  fetchDynamicPatientsList,
  DEFAULT_PATIENTS_LIST,
} from "../../services/ordersApi";
import "./CreateOrder.css";

const FALLBACK_MEDICINE_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='%23f1f5f9' stroke='%2394a3b8' stroke-width='1.5'%3E%3Crect width='100%25' height='100%25' fill='%23f8fafc'/%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E";

/**
 * Unified single searchable patient select component
 */
function PatientSearchableSelect({ patients, selectedId, onSelect, loading }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedPatient = useMemo(
    () => patients.find((p) => String(p.id) === String(selectedId)),
    [patients, selectedId]
  );

  // Multi-field search: by patient name, patientId/patientNumber (e.g. OMCA38283), phone, country, and userId
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return patients;
    const term = searchTerm.toLowerCase().trim();
    const cleanPhoneTerm = term.replace(/[^0-9]/g, "");

    return patients.filter((p) => {
      const name = String(p.name || "").toLowerCase();
      const pNum = String(p.patientNumber || p.patientId || "").toLowerCase();
      const rawPhone = String(p.phone || "").replace(/[^0-9]/g, "");
      const userId = String(p.userId || p.id || "").toLowerCase();
      const country = String(p.country || "").toLowerCase();

      return (
        name.includes(term) ||
        pNum.includes(term) ||
        (cleanPhoneTerm && rawPhone.includes(cleanPhoneTerm)) ||
        userId.includes(term) ||
        country.includes(term)
      );
    });
  }, [patients, searchTerm]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (patient) => {
    onSelect(String(patient.id));
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm("");
    inputRef.current?.focus();
  };

  const displayValue = isOpen
    ? searchTerm
    : selectedPatient
    ? `${selectedPatient.name}${
        selectedPatient.patientNumber ? ` (${selectedPatient.patientNumber})` : ""
      }`
    : "";

  return (
    <div ref={containerRef} className="patient-searchable-select">
      <div
        className={`patient-searchable-control ${isOpen ? "is-focused" : ""}`}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <i className="fa fa-search patient-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="patient-search-input"
          placeholder={
            loading
              ? "Loading all patients..."
              : "Search by Name, Patient ID (OMCA...), Phone..."
          }
          value={displayValue}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm("");
          }}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          autoComplete="off"
          disabled={loading}
        />
        {searchTerm && (
          <button
            type="button"
            className="patient-search-clear"
            onClick={handleClear}
            tabIndex={-1}
            title="Clear search"
          >
            ×
          </button>
        )}
        <i className={`fa fa-chevron-${isOpen ? "up" : "down"} patient-select-chevron`} />
      </div>

      {isOpen && (
        <div className="patient-searchable-dropdown" style={{ maxHeight: "360px", overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div className="patient-dropdown-empty p-3 text-center text-muted">
              No patient found matching "{searchTerm}"
            </div>
          ) : (
            <div className="patient-dropdown-list">
              {filtered.slice(0, 250).map((p) => {
                const isSelected = String(p.id) === String(selectedId);
                return (
                  <div
                    key={p.id}
                    className={`patient-dropdown-option ${isSelected ? "selected" : ""}`}
                    onMouseDown={() => handleSelect(p)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 14px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="patient-option-name fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                          {p.name}
                        </span>
                        {(p.patientNumber || p.patientId) && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#e0f2fe",
                              color: "#0369a1",
                              border: "1px solid #bae6fd",
                              fontSize: "11px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            {p.patientNumber || p.patientId}
                          </span>
                        )}
                        {p.source && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#f8fafc",
                              color: "#64748b",
                              border: "1px solid #e2e8f0",
                              fontSize: "10px",
                              padding: "1px 5px",
                            }}
                          >
                            {p.source}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-3 mt-1 text-muted" style={{ fontSize: "11.5px" }}>
                        {p.phone && p.phone !== "-" && (
                          <span>
                            <i className="fa fa-phone me-1 text-secondary" style={{ fontSize: "10px" }} />
                            {p.phone}
                          </span>
                        )}
                        {p.country && (
                          <span>
                            <i className="fa fa-globe me-1 text-secondary" style={{ fontSize: "10px" }} />
                            {p.country}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && <i className="fa fa-check text-primary ms-2" />}
                  </div>
                );
              })}
              {filtered.length > 250 && (
                <div className="text-center py-2 small text-muted bg-light border-top sticky-bottom">
                  Showing first 250 of {filtered.length} matching patients. Type to refine search.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreateOrder() {
  const navigate = useNavigate();

  // Active CRM Tab: 'cart' | 'catalog' | 'upload'
  const [activeTab, setActiveTab] = useState("cart");

  // Cart sub-view: 'keyed_in' | 'prescribed'
  const [cartViewType, setCartViewType] = useState("prescribed");

  // Patients / Customers available dynamically across CRM & Pharmacy App
  const [patientsList, setPatientsList] = useState(DEFAULT_PATIENTS_LIST || []);
  const [loadingPatients, setLoadingPatients] = useState(true);

  const [selectedPatientId, setSelectedPatientId] = useState("");

  // Fetch dynamic patients list from CRM database and Orders history
  useEffect(() => {
    let isMounted = true;
    const loadDynamicPatients = async () => {
      setLoadingPatients(true);
      try {
        const dynamicList = await fetchDynamicPatientsList();
        if (isMounted && Array.isArray(dynamicList) && dynamicList.length > 0) {
          setPatientsList(dynamicList);
        }
      } catch (err) {
        console.warn("Dynamic patients load error:", err);
      } finally {
        if (isMounted) setLoadingPatients(false);
      }
    };
    loadDynamicPatients();
    return () => {
      isMounted = false;
    };
  }, []);

  const selectedPatient = useMemo(
    () =>
      selectedPatientId
        ? patientsList.find((p) => String(p.id) === String(selectedPatientId)) || {
            id: selectedPatientId,
            name: `User #${selectedPatientId}`,
            phone: "—",
            email: `user${selectedPatientId}@omcacrm.com`,
            defaultAddress: "Mauritius",
            source: "CRM Customer",
          }
        : null,
    [patientsList, selectedPatientId]
  );

  // Always use the patient's numeric pharmacy user_id (e.g. "1720") for backend APIs, never patientId ("OMCA26070666")
  const effectiveUserId = useMemo(() => {
    return String(selectedPatient?.userId || selectedPatient?.user_id || selectedPatientId || "").trim();
  }, [selectedPatient, selectedPatientId]);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  // KeyedIn medicines (loaded from backend cartlist API)
  const [keyedInMedicines, setKeyedInMedicines] = useState([]);
  const [loadingMedicineCart, setLoadingMedicineCart] = useState(false);

  // Prescribed items (loaded from backend cart_list API)
  const [prescribedItems, setPrescribedItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);

  // Upload screen state (upload_prescription API)
  // uploadType: 0 = Prescription, 1 = Medicine Photo
  const [uploadType, setUploadType] = useState(0);

  const createInitialCard = (isPrescription = true, id = 1) => ({
    id,
    frontFile: null,
    frontFileName: "",
    frontPreview: "",
    backFile: null,
    backFileName: "",
    backPreview: "",
    asPerPrescription: isPrescription,
    days: "",
  });

  const [prescriptionCards, setPrescriptionCards] = useState([
    createInitialCard(true, 1),
  ]);
  const [medicineCards, setMedicineCards] = useState([
    createInitialCard(false, 1),
  ]);
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false);

  // Medicine catalog state (get_medicine_list API)
  const [catalogMedicines, setCatalogMedicines] = useState([]);
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [loadingMedicines, setLoadingMedicines] = useState(false);

  // Order submission state
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Load addresses, medicine cart (cartlist), prescribed cart (cart_list), and medicines whenever selected patient changes
  useEffect(() => {
    if (effectiveUserId) {
      loadUserAddresses(effectiveUserId);
      loadMedicineCart(effectiveUserId);
      loadCart(effectiveUserId);
    } else {
      setAddresses([]);
      setSelectedAddressId(null);
      setKeyedInMedicines([]);
      setPrescribedItems([]);
    }
    loadMedicines(effectiveUserId || "675");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserId]);

  // Load user addresses
  const loadUserAddresses = async (userId) => {
    if (!userId) {
      setAddresses([]);
      setSelectedAddressId(null);
      return;
    }
    try {
      const res = await fetchUserAddresses(userId);
      if (res?.data && res.data.length > 0) {
        setAddresses(res.data);
        setSelectedAddressId(res.data[0].id);
      } else {
        setAddresses([]);
        setSelectedAddressId(null);
      }
    } catch (e) {
      console.warn("Addresses fetch error:", e);
      setAddresses([]);
      setSelectedAddressId(null);
    }
  };

  // Load medicine cart items from `cartlist` API
  const loadMedicineCart = async (userId) => {
    if (!userId) {
      setKeyedInMedicines([]);
      return;
    }
    setLoadingMedicineCart(true);
    try {
      const res = await fetchMedicineCartList(userId);
      if (res?.data && Array.isArray(res.data)) {
        const mapped = res.data.map((item) => {
          const medId = Number(item.medicine_id || item.id);
          const cartId = item.id;
          const unitPrice = parseFloat(item.converted_price || item.price || 40.44);
          const qty = Number(item.quantity) || 1;
          const totalPrice = parseFloat(item.converted_total || item.total || unitPrice * qty);
          return {
            id: medId,
            cart_id: cartId,
            medicine_id: medId,
            name: item.name,
            manufacture: item.manufacture || "",
            quantity: item.medicine_quantity || "15 tablets in a strip",
            cartQuantity: qty,
            unitPrice: unitPrice,
            totalPrice: totalPrice,
            currency_symbol: item.currency_symbol || "₹",
            image:
              item.image ||
              "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg",
            raw: item,
          };
        });
        setKeyedInMedicines(mapped);
      } else {
        setKeyedInMedicines([]);
      }
    } catch (e) {
      console.warn("Medicine cart (cartlist) load error:", e);
      setKeyedInMedicines([]);
    } finally {
      setLoadingMedicineCart(false);
    }
  };

  // Load prescribed cart items from `cart_list` API
  const loadCart = async (userId) => {
    if (!userId) {
      setPrescribedItems([]);
      return;
    }
    setLoadingCart(true);
    try {
      const res = await fetchCartList(userId);
      if (res?.data && Array.isArray(res.data)) {
        setPrescribedItems(res.data);
      } else {
        setPrescribedItems([]);
      }
    } catch (e) {
      console.warn("Prescription cart (cart_list) load error:", e);
      setPrescribedItems([]);
    } finally {
      setLoadingCart(false);
    }
  };

  // Load medicine catalog
  const loadMedicines = async (userId) => {
    setLoadingMedicines(true);
    try {
      const res = await fetchMedicineList(userId);
      if (res?.data && res.data.length > 0) {
        setCatalogMedicines(res.data);
      } else {
        // High fidelity catalog data matching backend
        setCatalogMedicines([
          {
            id: 1,
            name: "Dolo",
            quantity: "15 tablets in a strip",
            price: "20",
            converted_amount: "40.44",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg",
          },
          {
            id: 2,
            name: "Paracetamal",
            quantity: "15 tablets in a strip",
            price: "15",
            converted_amount: "30.33",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/download (2).jpeg",
          },
          {
            id: 4,
            name: "Crocin",
            quantity: "15 tablets in a strip",
            price: "20",
            converted_amount: "40.44",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg",
          },
          {
            id: 5,
            name: "Becosules",
            quantity: "20 tablets in a strip",
            price: "150",
            converted_amount: "303.32",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/01717501972.jpg",
          },
          {
            id: 6,
            name: "Corex",
            quantity: "1 tablets in a strip",
            price: "120",
            converted_amount: "242.65",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/01717503565.jpg",
          },
          {
            id: 15,
            name: "Aspirin",
            quantity: "10 tablets in a strip",
            price: "42",
            converted_amount: "84.93",
            currency_symbol: "₹",
            image: "https://omcacrm.com/omca/public/images/medicineImages/01779704221.jpg",
          },
        ]);
      }
    } catch (e) {
      console.warn("Medicines load error:", e);
    } finally {
      setLoadingMedicines(false);
    }
  };

  // =========================================================================
  // MEDICINES CATALOG HANDLERS
  // =========================================================================

  const handleToggleAddMedicine = async (med) => {
    if (!effectiveUserId) {
      Swal.fire({
        title: "Patient Required",
        text: "Please select a patient / customer first before adding medicines to cart.",
        icon: "warning",
        confirmButtonColor: "#0ba6df",
      });
      return;
    }

    const exists = keyedInMedicines.some(
      (item) => Number(item.id) === Number(med.id) || Number(item.medicine_id) === Number(med.id)
    );

    // Calls cartValue with user_id and medicine_id to toggle add/remove in backend cart
    const res = await toggleCartValueApi(effectiveUserId, med.id);
    // Reload medicine cart list from `cartlist` API
    await loadMedicineCart(effectiveUserId);

    if (exists) {
      Swal.fire({
        title: "Removed",
        text: res?.message || `${med.name} removed from cart.`,
        icon: "info",
        timer: 1200,
        showConfirmButton: false,
      });
    } else {
      Swal.fire({
        title: "Added to Cart!",
        text: res?.message || `${med.name} added to cart.`,
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    }
  };

  const filteredMedicines = useMemo(() => {
    if (!medicineSearchTerm.trim()) return catalogMedicines;
    const q = medicineSearchTerm.toLowerCase().trim();
    return catalogMedicines.filter(
      (m) =>
        String(m.name || "").toLowerCase().includes(q) ||
        String(m.quantity || "").toLowerCase().includes(q) ||
        String(m.category || "").toLowerCase().includes(q)
    );
  }, [catalogMedicines, medicineSearchTerm]);

  // =========================================================================
  // CART STEPPER, DELETE & EMPTY CART HANDLERS
  // =========================================================================

  const handleUpdateKeyedInQty = async (medId, delta) => {
    const item = keyedInMedicines.find(
      (m) => Number(m.id) === Number(medId) || Number(m.medicine_id) === Number(medId)
    );
    if (!item) return;
    const nextQty = Math.max(1, (item.cartQuantity || 1) + delta);
    // Optimistic UI update
    setKeyedInMedicines((prev) =>
      prev.map((it) =>
        Number(it.id) === Number(medId) || Number(it.medicine_id) === Number(medId)
          ? { ...it, cartQuantity: nextQty }
          : it
      )
    );
    if (effectiveUserId) {
      await changeMedicineQuantity(effectiveUserId, item.medicine_id || medId, nextQty);
      await loadMedicineCart(effectiveUserId);
    }
  };

  const handleDeleteKeyedInItem = async (medId) => {
    const itemToDelete = keyedInMedicines.find(
      (item) => Number(item.id) === Number(medId) || Number(item.medicine_id) === Number(medId)
    );
    if (effectiveUserId) {
      // Calls cartValue with user_id and medicine_id on item delete as requested
      const res = await toggleCartValueApi(effectiveUserId, itemToDelete?.medicine_id || medId);
      await loadMedicineCart(effectiveUserId);
      Swal.fire({
        title: "Removed",
        text: res?.message || (itemToDelete ? `${itemToDelete.name} removed from cart.` : "Item removed from cart."),
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } else {
      setKeyedInMedicines((prev) =>
        prev.filter((item) => Number(item.id) !== Number(medId) && Number(item.medicine_id) !== Number(medId))
      );
      Swal.fire({
        title: "Removed",
        text: itemToDelete ? `${itemToDelete.name} removed from cart.` : "Item removed from cart.",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    }
  };

  const handleEmptyCart = async () => {
    if (keyedInMedicines.length === 0 && prescribedItems.length === 0) {
      Swal.fire("Cart is Empty", "There are no items in the cart to remove.", "info");
      return;
    }

    const confirmed = await Swal.fire({
      title: "Empty Cart?",
      text: "Are you sure you want to empty the entire cart? All items will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Empty Cart",
      cancelButtonText: "Cancel",
    });

    if (confirmed.isConfirmed) {
      if (effectiveUserId) {
        // Calls cart_item_delete API with user_id to empty cart in backend
        await deleteCartItem(effectiveUserId);
        await Promise.all([
          loadMedicineCart(effectiveUserId),
          loadCart(effectiveUserId),
        ]);
      } else {
        setKeyedInMedicines([]);
        setPrescribedItems([]);
      }
      Swal.fire({
        title: "Cart Emptied",
        text: "All items have been removed from the cart.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleDeletePrescriptionItem = async (cartItemId) => {
    const confirmed = await Swal.fire({
      title: "Remove Item?",
      text: "Remove this prescribed item from the order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove",
    });

    if (confirmed.isConfirmed) {
      if (effectiveUserId) {
        await deleteCartPrescription(effectiveUserId, cartItemId);
        await loadCart(effectiveUserId);
      } else {
        setPrescribedItems((prev) => prev.filter((item) => item.id !== cartItemId));
      }
      Swal.fire({
        title: "Removed",
        text: "Item removed from cart.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  // Cart totals calculation
  const totals = useMemo(() => {
    const totalMedicinesQty = keyedInMedicines.reduce(
      (sum, item) => sum + (item.cartQuantity || 1),
      0
    );
    const totalItems = totalMedicinesQty + prescribedItems.length;

    return {
      keyedInMedicinesCount: keyedInMedicines.length,
      totalMedicinesQty,
      prescribedCount: prescribedItems.length,
      totalItems,
      totalAmount: "0.00",
    };
  }, [keyedInMedicines, prescribedItems]);

  // Comma-separated IDs with space from cart_list API for place_order 'prescription' key (e.g. "2, 5, 6")
  const prescriptionParam = useMemo(() => {
    return prescribedItems
      .map((item) => String(item.id).trim())
      .filter(Boolean)
      .join(", ");
  }, [prescribedItems]);

  // Comma-separated IDs with space from cartlist API for place_order 'cart' key (e.g. "2, 3, 5")
  const cartParam = useMemo(() => {
    return keyedInMedicines
      .map((item) => String(item.cart_id || item.raw?.id || item.id).trim())
      .filter(Boolean)
      .join(", ");
  }, [keyedInMedicines]);

  // =========================================================================
  // UPLOAD SCREEN (upload_prescription API)
  // =========================================================================

  // Current active cards list and setter depending on active sub-tab
  const currentUploadCards = uploadType === 0 ? prescriptionCards : medicineCards;
  const setCurrentUploadCards = uploadType === 0 ? setPrescriptionCards : setMedicineCards;

  // Counts of valid items in each category
  const validPrescriptionCards = useMemo(
    () => prescriptionCards.filter((c) => c.frontFile || c.backFile),
    [prescriptionCards]
  );
  const validMedicineCards = useMemo(
    () => medicineCards.filter((c) => c.frontFile || c.backFile),
    [medicineCards]
  );
  const totalUploadItemsCount = validPrescriptionCards.length + validMedicineCards.length;

  const handleFileChange = (cardId, side, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setCurrentUploadCards((prev) =>
      prev.map((c) => {
        if (c.id === cardId) {
          return side === "front"
            ? { ...c, frontFile: file, frontFileName: file.name, frontPreview: previewUrl }
            : { ...c, backFile: file, backFileName: file.name, backPreview: previewUrl };
        }
        return c;
      })
    );
  };

  const handleRemoveFile = (cardId, side) => {
    setCurrentUploadCards((prev) =>
      prev.map((c) => {
        if (c.id === cardId) {
          return side === "front"
            ? { ...c, frontFile: null, frontFileName: "", frontPreview: "" }
            : { ...c, backFile: null, backFileName: "", backPreview: "" };
        }
        return c;
      })
    );
  };

  const handleCardFieldChange = (cardId, field, value) => {
    setCurrentUploadCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, [field]: value } : c))
    );
  };

  const handleAddAnotherUploadCard = () => {
    setCurrentUploadCards((prev) => {
      const nextId = prev.length > 0 ? Math.max(...prev.map((c) => c.id)) + 1 : 1;
      return [
        ...prev,
        uploadType === 0
          ? createInitialCard(true, nextId)
          : createInitialCard(false, nextId),
      ];
    });
  };

  const handleDeleteUploadCard = (cardId) => {
    if (currentUploadCards.length <= 1) {
      Swal.fire("Notice", "At least one item is required.", "info");
      return;
    }
    setCurrentUploadCards((prev) => prev.filter((c) => c.id !== cardId));
  };

  const handleSaveAndProceedUpload = async () => {
    if (!selectedPatientId) {
      Swal.fire({
        icon: "warning",
        title: "Patient Required",
        text: "Please select a Patient / Customer first before uploading files.",
        confirmButtonColor: "#0ba6df",
      });
      return;
    }

    // Collect all valid cards from BOTH prescription and medicine photo sections
    const allValidItems = [
      ...validPrescriptionCards.map((c) => ({ ...c, type: 0 })),
      ...validMedicineCards.map((c) => ({ ...c, type: 1 })),
    ];

    if (allValidItems.length === 0) {
      Swal.fire(
        "Image Required",
        "Please upload at least a Front or Back image for at least one item.",
        "warning"
      );
      return;
    }

    setIsSubmittingUpload(true);
    try {
      const formData = new FormData();
      formData.append("user_id", String(effectiveUserId));

      allValidItems.forEach((card, index) => {
        const qty =
          card.type === 0
            ? (card.asPerPrescription ? "0" : (card.days || "1"))
            : (card.days || "1");

        formData.append(`prescriptions[${index}][quantity]`, String(qty));
        formData.append(`prescriptions[${index}][type]`, String(card.type));

        if (card.frontFile) {
          formData.append(`prescriptions[${index}][front_image]`, card.frontFile);
        }
        if (card.backFile) {
          formData.append(`prescriptions[${index}][back_image]`, card.backFile);
        }
      });

      const res = await uploadPrescriptionApi(formData);
      if (res.success) {
        const pCount = validPrescriptionCards.length;
        const mCount = validMedicineCards.length;
        let successMsg = "";
        if (pCount > 0 && mCount > 0) {
          successMsg = `${pCount} prescription(s) and ${mCount} medicine photo(s) uploaded and added to cart.`;
        } else if (pCount > 0) {
          successMsg = `${pCount} prescription(s) uploaded and added to cart.`;
        } else {
          successMsg = `${mCount} medicine photo(s) uploaded and added to cart.`;
        }

        Swal.fire({
          title: "Uploaded Successfully!",
          text: res.message || successMsg,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Reset both sections to clean initial states
        setPrescriptionCards([createInitialCard(true, 1)]);
        setMedicineCards([createInitialCard(false, 1)]);

        await loadCart(effectiveUserId);

        // Switch to Cart Items tab and prescribed sub-view
        setCartViewType("prescribed");
        setActiveTab("cart");
      } else {
        Swal.fire("Upload Notice", res.message || "Could not upload items.", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Failed to upload prescriptions/medicines.", "error");
    } finally {
      setIsSubmittingUpload(false);
    }
  };

  // =========================================================================
  // PLACE ORDER HANDLER (`place_order` API)
  // =========================================================================

  const handlePlaceOrder = async () => {
    if (!effectiveUserId || !selectedPatient) {
      Swal.fire({
        title: "Patient Required",
        text: "Patient / Customer is mandatory for order. Please select a patient first.",
        icon: "warning",
        confirmButtonColor: "#0ba6df",
      });
      return;
    }

    if (totals.totalItems === 0) {
      Swal.fire(
        "Cart is Empty",
        "Please add catalog medicines or upload prescriptions before placing an order.",
        "warning"
      );
      return;
    }

    let addressIdToUse = selectedAddressId;
    if (!addressIdToUse) {
      if (addresses.length > 0) {
        addressIdToUse = addresses[0].id;
      } else {
        try {
          const addrRes = await saveUserAddress({
            user_id: effectiveUserId,
            house_name: selectedPatient.defaultAddress.split(",")[0] || "House 1",
            street: selectedPatient.defaultAddress.split(",")[1] || "Main Road",
            city: "Curepipe",
            state: "Plaines Wilhems",
            country: "Mauritius",
            pincode: "74204",
            mobile: selectedPatient.phone.replace(/[^0-9]/g, "").slice(-8) || "57891234",
            mobile_code: "230",
          });
          if (addrRes.success && addrRes.data) {
            addressIdToUse = addrRes.data.id;
          } else {
            addressIdToUse = 112;
          }
        } catch (e) {
          addressIdToUse = 112;
        }
      }
    }

    const totalItem = totals.totalItems;
    const addressID = addressIdToUse;
    const method = "0";

    const param = {
      user: Number(effectiveUserId) || effectiveUserId || 0,
      user_id: Number(effectiveUserId) || effectiveUserId || 0,
      cart: cartParam,
      quantity: totalItem,
      price: 0,
      prescription: prescriptionParam,
      address: addressID,
      method: method,
    };

    const confirm = await Swal.fire({
      title: "Confirm Order Placement?",
      text: `Place order for ${selectedPatient.name} with ${totalItem} item(s) (${keyedInMedicines.length} medicine(s), ${prescribedItems.length} prescription/photo(s))?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0ba6df",
      confirmButtonText: "Yes, Place Order",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setIsPlacingOrder(true);
    try {
      const res = await placeOrderApi(param);

      if (!res.success) {
        Swal.fire("Order Notice", res.message || "Could not complete order placement.", "warning");
        return;
      }

      const newOrderId = String(Math.floor(100000000000 + Math.random() * 900000000000));
      const createdOrder = {
        id: newOrderId,
        sNo: 1,
        orderId: newOrderId,
        patientName: selectedPatient.name,
        userId: effectiveUserId,
        patientPhone: selectedPatient.phone,
        patientEmail: selectedPatient.email,
        deliveryAddress:
          addresses.find((a) => a.id === addressIdToUse)?.street ||
          selectedPatient.defaultAddress,
        paymentMode: "Prescription Fulfillment",
        paidAmount: 0,
        currency: "MUR",
        orderDate: new Date().toISOString().replace("T", " ").slice(0, 19),
        status: "Pending",
        notes: `Order created via CRM by Admin for ${selectedPatient.name} (user: ${param.user}, cart: ${param.cart || 'none'}, prescription: ${param.prescription || 'none'})`,
        items: [
          ...keyedInMedicines.map((m) => ({
            id: `it-${m.id}`,
            name: m.name,
            category: "Medicine",
            dosage: m.quantity || "Standard",
            quantity: m.cartQuantity || 1,
            unitPrice: 0,
            total: 0,
            image:
              m.image ||
              "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg",
          })),
          ...prescribedItems.map((p, idx) => ({
            id: `pr-${p.id || idx}`,
            name:
              Number(p.type) === 1
                ? `Prescribed Medicine #${idx + 1}`
                : `Prescription #${idx + 1}`,
            category: Number(p.type) === 1 ? "Medicine Photo" : "Prescription",
            dosage: `${p.quantity || p.front_quantity || 1} Days`,
            quantity: 1,
            unitPrice: 0,
            total: 0,
            type: p.type,
            image:
              p.front_image ||
              p.back_image ||
              "https://omcacrm.com/omca/public/images/prescription/1788841526_6a9f8e3611987_front.jpg",
            front_image: p.front_image || "",
            back_image: p.back_image || "",
          })),
        ],
        prescriptions: prescribedItems.map((p, idx) => ({
          id: p.id || idx + 1,
          name:
            Number(p.type) === 1
              ? `Prescribed Medicine #${idx + 1}`
              : `Prescription Copy #${idx + 1}`,
          type: p.type,
          quantity: p.quantity || p.front_quantity || 1,
          front_image:
            p.front_image ||
            "https://omcacrm.com/omca/public/images/prescription/1788841526_6a9f8e3611987_front.jpg",
          back_image: p.back_image || "",
        })),
      };

      addNewOrder(createdOrder);

      Swal.fire({
        title: "Order Placed Successfully!",
        text: `Order #${newOrderId} has been registered and marked Pending.`,
        icon: "success",
        confirmButtonColor: "#0ba6df",
      }).then(() => {
        navigate("/Admin/orders");
      });
    } catch (err) {
      console.error("Order placement error:", err);
      Swal.fire("Error", "Could not complete order placement.", "error");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const selectedAddressObj = addresses.find((a) => a.id === selectedAddressId);

  return (
    <div className="page-wrapper">
      <div className="content create-order-container">
        {/* Page Header */}
        <div className="create-order-header">
          <div>
            <h3>Create New Order</h3>
            <ul className="create-order-breadcrumb">
              <li>
                <Link to="/Dashboard">Dashboard</Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/Admin/orders">Orders</Link>
              </li>
              <li>/</li>
              <li>Create Order</li>
            </ul>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
              onClick={() => navigate("/Admin/orders")}
            >
              <i className="fa fa-arrow-left" /> Back to Orders
            </button>
          </div>
        </div>

        {/* Top Patient & Delivery Configuration Bar */}
        <div className="order-patient-bar">
          <div className="order-patient-grid">
            {/* Patient Select */}
            <div>
              <div className="patient-input-label">
                <span>
                  Patient / Customer <span className="text-danger fw-bold">*</span>{" "}
                  {loadingPatients ? "(Loading...)" : `(${patientsList.length} total)`}
                </span>
                {loadingPatients && (
                  <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                )}
              </div>

              <PatientSearchableSelect
                patients={patientsList}
                selectedId={selectedPatientId}
                onSelect={(newId) => setSelectedPatientId(newId)}
                loading={loadingPatients}
              />

              {selectedPatient ? (
                <div className="patient-subinfo flex-wrap">
                  <span>
                    <i className="fa fa-user me-1" />
                    <strong>{selectedPatient.name}</strong>
                    {(selectedPatient.patientNumber || selectedPatient.patientId) && (
                      <span
                        className="badge bg-primary-subtle text-primary border ms-1.5"
                        style={{ fontSize: "11px", fontWeight: 600, padding: "2px 6px" }}
                      >
                        ID: {selectedPatient.patientNumber || selectedPatient.patientId}
                      </span>
                    )}
                    {selectedPatient.source && (
                      <span
                        className="badge bg-soft-info text-info ms-1"
                        style={{ fontSize: "10px", fontWeight: 600, border: "1px solid rgba(11, 166, 223, 0.3)" }}
                      >
                        {selectedPatient.source}
                      </span>
                    )}
                  </span>
                  <span>
                    <i className="fa fa-phone me-1" />
                    {selectedPatient.phone}
                  </span>
                  <span>
                    <i className="fa fa-envelope-o me-1" />
                    {selectedPatient.email}
                  </span>
                </div>
              ) : (
                <div className="text-danger small mt-2 d-flex align-items-center gap-1 fw-semibold">
                  <i className="fa fa-exclamation-circle" /> Patient / Customer is mandatory for order
                </div>
              )}
            </div>

            {/* Address Select */}
            <div>
              <div className="patient-input-label">
                <span>Delivery Address</span>
              </div>

              <select
                className="patient-select-ctrl"
                value={selectedAddressId || ""}
                onChange={(e) => setSelectedAddressId(e.target.value)}
                disabled={!selectedPatientId}
              >
                {!selectedPatientId ? (
                  <option value="">Select a patient / customer first...</option>
                ) : addresses.length > 0 ? (
                  addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.house || a.house_name ? `${a.house || a.house_name}, ` : ""}
                      {a.street}, {a.city}, {a.counrty || a.country}
                    </option>
                  ))
                ) : (
                  <option value="">{selectedPatient?.defaultAddress || "No saved address found"}</option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Main Workspace Layout */}
        <div className="order-workspace-layout">
          {/* Main Card: Tabbed Workflow */}
          <div className="order-content-card">
            {/* Clean Tab Header (No Horizontal Scrollbar!) */}
            <div className="order-tabs-header">
              <button
                type="button"
                className={`order-tab-item ${activeTab === "cart" ? "active" : ""}`}
                onClick={() => setActiveTab("cart")}
              >
                <i className="fa fa-shopping-cart" />
                <span>Cart</span>
                <span className="order-tab-count">
                  {keyedInMedicines.length + prescribedItems.length}
                </span>
              </button>

              <button
                type="button"
                className={`order-tab-item ${activeTab === "catalog" ? "active" : ""}`}
                onClick={() => setActiveTab("catalog")}
              >
                <i className="fa fa-medkit" />
                <span>Medicines Catalog</span>
                <span className="order-tab-count">{catalogMedicines.length}</span>
              </button>

              <button
                type="button"
                className={`order-tab-item ${activeTab === "upload" ? "active" : ""}`}
                onClick={() => setActiveTab("upload")}
              >
                <i className="fa fa-cloud-upload" />
                <span>Upload Prescriptions</span>
              </button>
            </div>

            {/* TAB 1: CART ITEMS */}
            {activeTab === "cart" && (
              <div className="order-tab-body">
                <div className="cart-filter-pills">
                  <div className="pill-group">
                    <button
                      type="button"
                      className={`pill-btn ${cartViewType === "keyed_in" ? "active" : ""}`}
                      onClick={() => setCartViewType("keyed_in")}
                    >
                      Keyed-In Medicines ({keyedInMedicines.length})
                    </button>
                    <button
                      type="button"
                      className={`pill-btn ${cartViewType === "prescribed" ? "active" : ""}`}
                      onClick={() => setCartViewType("prescribed")}
                    >
                      Prescribed Items ({prescribedItems.length})
                    </button>
                  </div>

                  <div className="cart-action-links">
                    <button
                      type="button"
                      className="btn-crm-sm outline"
                      onClick={() => setActiveTab("catalog")}
                    >
                      <i className="fa fa-plus-circle" /> Add Medicines
                    </button>
                    <button
                      type="button"
                      className="btn-crm-sm outline"
                      onClick={() => setActiveTab("upload")}
                    >
                      <i className="fa fa-cloud-upload" /> Upload Prescription
                    </button>
                    {(keyedInMedicines.length > 0 || prescribedItems.length > 0) && (
                      <button
                        type="button"
                        className="btn-empty-cart"
                        onClick={handleEmptyCart}
                        title="Empty all items from cart"
                      >
                        <i className="fa fa-trash-o" /> Empty Cart
                      </button>
                    )}
                  </div>
                </div>

                {/* Sub-view: Keyed-In Medicines Table */}
                {cartViewType === "keyed_in" && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="small text-muted">
                        <i className="fa fa-info-circle text-primary me-1" />
                        {selectedPatient ? (
                          <>
                            Showing medicines in cart for <strong>{selectedPatient.name}</strong> ({keyedInMedicines.length} items)
                          </>
                        ) : (
                          <span>Please select a patient / customer above to view cart medicines</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => effectiveUserId && loadMedicineCart(effectiveUserId)}
                        disabled={!effectiveUserId}
                      >
                        <i className="fa fa-refresh me-1" /> Refresh Cart
                      </button>
                    </div>

                    {loadingMedicineCart ? (
                      <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2" />
                        Loading medicines in cart...
                      </div>
                    ) : keyedInMedicines.length > 0 ? (
                      <div className="table-responsive">
                        <table className="crm-table">
                          <thead>
                            <tr>
                              <th style={{ width: "65px" }}>Item</th>
                              <th>Medicine Name & Packaging</th>
                              <th style={{ width: "130px" }} className="text-center">Quantity</th>
                              <th style={{ width: "60px" }} className="text-center">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {keyedInMedicines.map((item) => (
                              <tr key={item.id}>
                                <td>
                                  <img
                                    src={
                                      item.image ||
                                      "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg"
                                    }
                                    alt={item.name}
                                    className="med-thumb"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = FALLBACK_MEDICINE_IMAGE;
                                    }}
                                  />
                                </td>
                                <td>
                                  <div className="fw-bold text-dark">{item.name}</div>
                                  <span className="small text-muted">
                                    {item.quantity || "15 tablets in a strip"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="qty-stepper mx-auto">
                                    <button
                                      type="button"
                                      className="qty-btn"
                                      onClick={() => handleUpdateKeyedInQty(item.id, -1)}
                                    >
                                      -
                                    </button>
                                    <span className="qty-val">{item.cartQuantity || 1}</span>
                                    <button
                                      type="button"
                                      className="qty-btn"
                                      onClick={() => handleUpdateKeyedInQty(item.id, 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-link text-danger p-0"
                                    onClick={() => handleDeleteKeyedInItem(item.id)}
                                    title="Remove item"
                                  >
                                    <i className="fa fa-trash-o" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fa fa-shopping-basket fa-3x mb-2 text-muted" />
                        <h6 className="fw-bold">No Keyed-In Medicines in Cart</h6>
                        <p className="small mb-3">
                          Select products from the medicines catalog to add them to this order.
                        </p>
                        <button
                          type="button"
                          className="btn-crm-sm primary"
                          onClick={() => setActiveTab("catalog")}
                        >
                          <i className="fa fa-plus-circle" /> Browse Medicines Catalog
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Sub-view: Prescribed Items Grid */}
                {cartViewType === "prescribed" && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="small text-muted">
                        <i className="fa fa-info-circle text-primary me-1" />
                        {selectedPatient ? (
                          <>
                            Showing prescribed items for <strong>{selectedPatient.name}</strong> ({prescribedItems.length} items)
                          </>
                        ) : (
                          <span>Please select a patient / customer above to view prescribed items</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => effectiveUserId && loadCart(effectiveUserId)}
                        disabled={!effectiveUserId}
                      >
                        <i className="fa fa-refresh me-1" /> Refresh Cart
                      </button>
                    </div>

                    {loadingCart ? (
                      <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2" />
                        Loading prescribed items...
                      </div>
                    ) : prescribedItems.length > 0 ? (
                      <div className="prescribed-grid">
                        {prescribedItems.map((item, idx) => {
                          const isPrescription = Number(item.type) === 0;
                          const cardTitle = isPrescription
                            ? `Prescription #${idx + 1}`
                            : `Medicine Item #${idx + 1}`;
                          const displayDays =
                            item.quantity === "0" || !item.quantity
                              ? "As per prescription"
                              : `${item.quantity || item.front_quantity || 10} Days`;

                          const imageSrc =
                            item.front_image ||
                            item.back_image ||
                            "https://images.unsplash.com/photo-1576602976047-174e57a47881?w=300";

                          return (
                            <div key={item.id || idx} className="prescribed-item-box">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: isPrescription ? "#0ba6df" : "#10b981",
                                    color: "#fff",
                                  }}
                                >
                                  {isPrescription ? "Prescription" : "Medicine Photo"}
                                </span>
                                <span className="small text-muted fw-bold">ID: #{item.id}</span>
                                <button
                                  type="button"
                                  className="btn btn-link text-danger p-0"
                                  onClick={() => handleDeletePrescriptionItem(item.id)}
                                  title="Remove item"
                                >
                                  <i className="fa fa-trash-o" />
                                </button>
                              </div>

                              <div className="fw-bold text-dark mb-1">{cardTitle}</div>

                              <div className="d-flex gap-2 mb-2">
                                {item.front_image ? (
                                  <div style={{ flex: 1 }}>
                                    <img
                                      src={item.front_image}
                                      alt="Front"
                                      className="prescribed-thumb-img"
                                      style={{ height: "90px", objectFit: "cover" }}
                                    />
                                    <div className="text-center text-muted" style={{ fontSize: "10px" }}>Front Image</div>
                                  </div>
                                ) : (
                                  <img
                                    src={imageSrc}
                                    alt="Prescription"
                                    className="prescribed-thumb-img"
                                  />
                                )}
                                {item.back_image && (
                                  <div style={{ flex: 1 }}>
                                    <img
                                      src={item.back_image}
                                      alt="Back"
                                      className="prescribed-thumb-img"
                                      style={{ height: "90px", objectFit: "cover" }}
                                    />
                                    <div className="text-center text-muted" style={{ fontSize: "10px" }}>Back Image</div>
                                  </div>
                                )}
                              </div>

                              <div className="small text-muted d-flex justify-content-between align-items-center">
                                <span>
                                  <i className="fa fa-cubes text-primary me-1" />
                                  Qty: {item.quantity || 1}
                                </span>
                                <span>
                                  <i className="fa fa-calendar text-muted me-1" />
                                  {displayDays}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fa fa-file-text-o fa-3x mb-2 text-muted" />
                        <h6 className="fw-bold">No Prescriptions Uploaded</h6>
                        <p className="small mb-3">
                          Upload front/back photos of prescriptions or medicine strips{effectiveUserId ? ` for user #${effectiveUserId}` : ""}.
                        </p>
                        <button
                          type="button"
                          className="btn-crm-sm primary"
                          onClick={() => setActiveTab("upload")}
                        >
                          <i className="fa fa-cloud-upload" /> Upload Prescription
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: MEDICINES CATALOG */}
            {activeTab === "catalog" && (
              <div className="order-tab-body">
                {/* Search Bar */}
                <div className="catalog-search-wrap">
                  <i
                    className="fa fa-search text-muted position-absolute"
                    style={{ top: "13px", left: "14px" }}
                  />
                  <input
                    type="text"
                    className="catalog-search-input"
                    placeholder="Search medicines by name or packaging..."
                    value={medicineSearchTerm}
                    onChange={(e) => setMedicineSearchTerm(e.target.value)}
                  />
                </div>

                {/* Products Grid */}
                {loadingMedicines ? (
                  <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                    Loading medicines catalog...
                  </div>
                ) : filteredMedicines.length > 0 ? (
                  <div className="catalog-grid">
                    {filteredMedicines.map((med) => {
                      const isAdded = keyedInMedicines.some(
                        (item) =>
                          Number(item.id) === Number(med.id) ||
                          Number(item.medicine_id) === Number(med.id)
                      );
                      return (
                        <div key={med.id} className="catalog-item-card">
                          <div className="d-flex gap-2 align-items-center">
                            <img
                              src={
                                med.image ||
                                "https://omcacrm.com/omca/public/images/medicineImages/download (3).jpeg"
                              }
                              alt={med.name}
                              style={{
                                width: "54px",
                                height: "54px",
                                objectFit: "contain",
                                borderRadius: "6px",
                                border: "1px solid #e2e8f0",
                              }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = FALLBACK_MEDICINE_IMAGE;
                              }}
                            />
                            <div>
                              <div className="fw-bold text-dark">{med.name}</div>
                              <div className="small text-muted">
                                {med.quantity || "15 tablets in a strip"}
                              </div>
                            </div>
                          </div>

                          <div className="text-end pt-2 border-top">
                            {isAdded ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleToggleAddMedicine(med)}
                              >
                                <i className="fa fa-check me-1" /> Added (Remove)
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ backgroundColor: "#0ba6df", borderColor: "#0ba6df" }}
                                onClick={() => handleToggleAddMedicine(med)}
                              >
                                <i className="fa fa-plus me-1" /> Add to Order
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fa fa-medkit fa-3x mb-2 text-muted" />
                    <h6 className="fw-bold">No Medicines Found</h6>
                    <p className="small">Try searching for Dolo, Paracetamol, Crocin, or Becosules</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: UPLOAD PRESCRIPTION */}
            {activeTab === "upload" && (
              <div className="order-tab-body">
                {/* Upload Mode Pill */}
                <div className="upload-pills">
                  <button
                    type="button"
                    className={`upload-pill-btn ${uploadType === 0 ? "active" : ""}`}
                    onClick={() => setUploadType(0)}
                  >
                    <i className="fa fa-file-text-o me-1" /> Upload Prescription
                    {validPrescriptionCards.length > 0 && (
                      <span className="upload-pill-badge">
                        {validPrescriptionCards.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`upload-pill-btn ${uploadType === 1 ? "active" : ""}`}
                    onClick={() => setUploadType(1)}
                  >
                    <i className="fa fa-medkit me-1" /> Upload Medicine Photo
                    {validMedicineCards.length > 0 && (
                      <span className="upload-pill-badge">
                        {validMedicineCards.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Banner */}
                <div className="upload-alert-strip">
                  <i className="fa fa-info-circle text-primary" />
                  <span>
                    {uploadType === 0
                      ? "Add prescriptions with front or back image. Choose 'As per prescription' or optionally enter number of days."
                      : "Add medicines with front or back image (at least one) and number of days for each item."}
                  </span>
                </div>

                {/* Cards List */}
                <div>
                  {currentUploadCards.map((card, index) => {
                    const cardTitle =
                      uploadType === 0
                        ? `Prescription Item #${index + 1}`
                        : `Medicine Photo #${index + 1}`;

                    return (
                      <div key={card.id} className="upload-card-wrapper">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold text-dark m-0">{cardTitle}</h6>
                          {currentUploadCards.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() => handleDeleteUploadCard(card.id)}
                            >
                              <i className="fa fa-trash-o" /> Remove
                            </button>
                          )}
                        </div>

                        {/* Dropzones */}
                        <div className="upload-drop-grid">
                          {/* Front */}
                          <label className="dropzone-box">
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleFileChange(card.id, "front", e.target.files[0])
                              }
                            />
                            {card.frontPreview ? (
                              <>
                                <img
                                  src={card.frontPreview}
                                  alt="Front Preview"
                                  className="dropzone-preview-img"
                                />
                                <button
                                  type="button"
                                  className="dropzone-remove-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveFile(card.id, "front");
                                  }}
                                >
                                  <i className="fa fa-times" />
                                </button>
                              </>
                            ) : (
                              <>
                                <i className="fa fa-camera fa-2x text-muted mb-1" />
                                <span className="small fw-bold text-dark">Front Side Image</span>
                                <span className="text-muted" style={{ fontSize: "11px" }}>
                                  Click to upload Front Image
                                </span>
                              </>
                            )}
                          </label>

                          {/* Back */}
                          <label className="dropzone-box">
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleFileChange(card.id, "back", e.target.files[0])
                              }
                            />
                            {card.backPreview ? (
                              <>
                                <img
                                  src={card.backPreview}
                                  alt="Back Preview"
                                  className="dropzone-preview-img"
                                />
                                <button
                                  type="button"
                                  className="dropzone-remove-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveFile(card.id, "back");
                                  }}
                                >
                                  <i className="fa fa-times" />
                                </button>
                              </>
                            ) : (
                              <>
                                <i className="fa fa-camera fa-2x text-muted mb-1" />
                                <span className="small fw-bold text-dark">
                                  Back Side Image (Optional)
                                </span>
                                <span className="text-muted" style={{ fontSize: "11px" }}>
                                  Click to upload Back Image
                                </span>
                              </>
                            )}
                          </label>
                        </div>

                        {/* Options */}
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                          {uploadType === 0 && (
                            <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={card.asPerPrescription}
                                onChange={(e) =>
                                  handleCardFieldChange(
                                    card.id,
                                    "asPerPrescription",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="small fw-semibold">As per prescription</span>
                            </label>
                          )}

                          <div style={{ flex: 1, minWidth: "160px" }}>
                            <input
                              type="number"
                              min="1"
                              className="patient-select-ctrl"
                              placeholder={
                                uploadType === 0
                                  ? "Number of days (optional)"
                                  : "Number of days *"
                              }
                              value={card.days || ""}
                              disabled={uploadType === 0 && card.asPerPrescription}
                              onChange={(e) =>
                                handleCardFieldChange(card.id, "days", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Upload Actions */}
                <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                  <button
                    type="button"
                    className="btn-crm-sm outline"
                    onClick={handleAddAnotherUploadCard}
                  >
                    <i className="fa fa-plus-circle" /> Add Another Item
                  </button>

                  <button
                    type="button"
                    className="btn-crm-sm primary"
                    disabled={isSubmittingUpload}
                    onClick={handleSaveAndProceedUpload}
                  >
                    {isSubmittingUpload ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" /> Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-check-circle me-1" /> Save & Add to Order
                        {totalUploadItemsCount > 0 ? ` (${totalUploadItemsCount})` : ""}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Summary Sidebar */}
          <div className="order-summary-box">
            <div className="summary-heading">
              <i className="fa fa-list-alt text-primary" /> Order Summary
            </div>

            <div className="summary-row">
              <span>Patient:</span>
              <strong className="text-dark">
                {selectedPatient ? (
                  <>
                    {selectedPatient.name}
                    {(selectedPatient.patientNumber || selectedPatient.patientId) && (
                      <span className="text-muted ms-1" style={{ fontSize: "11px", fontWeight: "normal" }}>
                        ({selectedPatient.patientNumber || selectedPatient.patientId})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-muted fst-italic">Not selected</span>
                )}
              </strong>
            </div>

            <div className="summary-row">
              <span>Catalog Medicines:</span>
              <strong className="text-dark">{keyedInMedicines.length} items</strong>
            </div>

            <div className="summary-row">
              <span>Prescriptions / Photos:</span>
              <strong className="text-dark">{prescribedItems.length} items</strong>
            </div>

            <div className="summary-row total">
              <span>Total Items:</span>
              <span className="total-val" style={{ color: "#0ba6df" }}>{totals.totalItems}</span>
            </div>

            <div className="summary-address-preview">
              <div className="text-muted fw-bold mb-1" style={{ fontSize: "11px" }}>
                DELIVERY DESTINATION:
              </div>
              <div>
                {selectedAddressObj
                  ? `${selectedAddressObj.house || selectedAddressObj.house_name ? (selectedAddressObj.house || selectedAddressObj.house_name) + ", " : ""}${selectedAddressObj.street}, ${selectedAddressObj.city}`
                  : selectedPatient?.defaultAddress || "No patient selected"}
              </div>
            </div>

            <button
              type="button"
              className="btn-place-order-main"
              disabled={isPlacingOrder || totals.totalItems === 0 || !selectedPatientId}
              onClick={handlePlaceOrder}
              title={!selectedPatientId ? "Please select a patient / customer first" : "Place Order"}
            >
              {isPlacingOrder ? (
                <>
                  <span className="spinner-border spinner-border-sm" /> Placing Order...
                </>
              ) : (
                <>
                  <i className="fa fa-check" /> Place Order
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
